import { BaseMockBookingForm } from '@/React/Shared/Component/BaseMockBookingForm'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'
import useDate from '@/Shared/Hook/useDate'

interface BusinessNameGeneratorBookingPagePreviewProps {
    businessName: string
    businessTagline: string
    serviceName: string
    serviceDescription: string
    serviceDurationInMinutes: number
}

const calendarDays = Array.from({ length: 35 }, (_, index) => index + 1)

function getCalendarDayClasses(day: number): string {
    if (day === 9) {
        return 'h-9 rounded-lg border border-slate-300 bg-white shadow-xs'
    }

    if (day === 26 || day === 27 || day === 31) {
        return 'h-9 rounded-lg border border-slate-200 bg-white'
    }

    return 'h-9 rounded-lg bg-slate-100'
}

export function BusinessNameGeneratorBookingPagePreview({
    businessName,
    businessTagline,
    serviceName,
    serviceDescription,
    serviceDurationInMinutes,
}: BusinessNameGeneratorBookingPagePreviewProps) {
    const { getBrowserTimeZone, getCurrentMonthAndYear } = useDate()
    const browserTimeZone = getBrowserTimeZone()
    const currentMonthAndYear = getCurrentMonthAndYear()

    return (
        <BaseMockBookingForm.Container>
            <BaseMockBookingForm.AddressBar className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <Icon
                        name={IconNameEnum.Globe}
                        className="h-2.5 w-2.5"
                    />
                </div>
                <span className="truncate font-medium">
                    Booking page preview · Not published
                </span>
            </BaseMockBookingForm.AddressBar>
            <BaseMockBookingForm.Content>
                <div className="grid border-t border-slate-200 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                    <div className="space-y-5 p-5 md:p-6">
                        <header className="flex items-center gap-3">
                            <img
                                src="/static/images/profile.png"
                                alt=""
                                className="h-10 w-10 shrink-0 rounded-full object-cover"
                            />
                            <div className="min-w-0">
                                <h2 className="wrap-break-word text-sm font-semibold text-slate-900">
                                    {businessName}
                                </h2>
                                <p className="mt-1 text-xs text-slate-600">
                                    {businessTagline}
                                </p>
                            </div>
                        </header>

                        <div className="space-y-2">
                            <h3 className="text-lg font-bold leading-tight text-slate-900">
                                {serviceName}
                            </h3>
                            <p className="text-sm text-slate-600">
                                {serviceDescription}
                            </p>
                        </div>

                        <div className="space-y-2 text-sm text-slate-700">
                            <div className="flex items-center gap-2.5">
                                <img
                                    src="/static/icons/providers/google-meet.svg"
                                    alt=""
                                    className="h-4 w-4 shrink-0 rounded-sm"
                                />
                                <span>Google Meet</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                                <Icon
                                    name={IconNameEnum.Clock}
                                    className="h-4 w-4 shrink-0 text-slate-400"
                                />
                                <span>{serviceDurationInMinutes} minutes</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                                <Icon
                                    name={IconNameEnum.Globe}
                                    className="h-4 w-4 shrink-0 text-slate-400"
                                />
                                <span className="break-all">
                                    {browserTimeZone}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="min-w-0 space-y-4 border-t border-slate-200 p-5 md:border-t-0 md:p-6">
                        <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                            <span className="truncate">{browserTimeZone}</span>
                            <Icon
                                name={IconNameEnum.AngleDown}
                                className="h-3 w-3 shrink-0 text-slate-400"
                            />
                        </div>
                        <div
                            role="img"
                            aria-label={`Sample date-selection calendar for ${currentMonthAndYear}`}
                            className="space-y-4 rounded-xl border border-slate-200 p-4"
                        >
                            <div className="flex items-center justify-between gap-3 py-1 text-slate-400">
                                <Icon
                                    name={IconNameEnum.AngleLeft}
                                    className="h-3 w-3 shrink-0 text-slate-300"
                                />
                                <span className="text-center text-sm font-medium">
                                    {currentMonthAndYear}
                                </span>
                                <Icon
                                    name={IconNameEnum.AngleRight}
                                    className="h-3 w-3 shrink-0"
                                />
                            </div>
                            <div
                                className="grid grid-cols-7 gap-2"
                                aria-hidden="true"
                            >
                                {calendarDays.map((day) => (
                                    <div
                                        key={day}
                                        className={getCalendarDayClasses(day)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </BaseMockBookingForm.Content>
        </BaseMockBookingForm.Container>
    )
}
